import { Calendar, Input, InputProps, Modal } from '@/common/components/base-app';
import { Sizes, Variants } from '@/common/types';
import { styled } from '@/stitches.config';
import { ReactComponent as CalendarIcon } from '@assets/icons/calendar.svg';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { defaultDateFormatter } from './InputDateSelector.helper';
import { isValidDate } from '@/common/utils/date/date';
import { parse } from 'date-fns';
import { StyledCalendarContainer } from '@/common/components/base-app/molecules/Calendar/Calendar.styles';
import { StyledModalCalendarContainer } from '@/common/components/builder/organisms/InputDateSelector/InputDateSelector.styles';

const RootContainer = styled('div', {
  position: 'relative',
  width: '100%',
  cursor: 'pointer',
});

const IconButton = styled(CalendarIcon, {
  position: 'absolute',
  right: 0,
  bottom: '$1',
});

interface Props {
  title: string;
  inputProps: Omit<InputProps, 'label'>;
  onChange?: (newDate: Date | null) => void;
  formatDate?: (newDate: Date) => string;
  value?: Date;
  placeHolder?: string;
  secondaryLabel?: string;
  modalTitle?: string;
  lang?: string;
  direction?: 'horizontal' | 'vertical';
  dateFormat?: string;
  testId?: string;
}

export const InputDateSelector = ({
  inputProps,
  onChange,
  title,
  formatDate = defaultDateFormatter,
  value,
  placeHolder,
  secondaryLabel,
  modalTitle,
  lang = 'en',
  direction = 'vertical',
  dateFormat = 'MM/dd/yyyy',
  testId,
}: Props): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [dateAsString, setDateAsString] = useState(value ? formatDate(value) : '');
  const [currentDate, setCurrentDate] = useState<Date | null>(value ?? null);
  const [tempInputValue, setTempInputValue] = useState<string | null>('');

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value && dateAsString !== formatDate(value)) {
      handleChangeDate(value);
    }
  }, [value]);

  const handleChangeDate = (newDate: Date): void => {
    if (onChange) {
      onChange(newDate);
    }
    setIsOpen(false);
    setDateAsString(formatDate(newDate));
    setCurrentDate(newDate);
    setTempInputValue(null);
  };

  const onChangeInput = (e: ChangeEvent<HTMLInputElement>): void => {
    setTempInputValue(e.target.value);
    setDateAsString(e.target.value);
  };

  const handleManualDateValidate = (): void => {
    // If there is not date to validate, do nothing
    if (tempInputValue) {
      if (isValidDate(tempInputValue, dateFormat)) {
        const inputAsDate = parse(tempInputValue, dateFormat, new Date());
        handleChangeDate(inputAsDate);
      } else {
        setDateAsString('');
        setCurrentDate(null);
        setTempInputValue(null);
        if (onChange) {
          onChange(null);
        }
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleManualDateValidate();
      inputRef.current?.focus();
    }
  };

  return (
    <RootContainer>
      <IconButton onClick={() => setIsOpen(true)} css={inputProps.error ? { top: '$5' } : undefined} />
      <Input
        ref={inputRef}
        testId={testId}
        direction={direction}
        width="100%"
        css={{ ...inputProps.css }}
        {...inputProps}
        onClick={() => setIsOpen(true)}
        label={title}
        value={dateAsString ?? ''}
        variant={Variants.PRIMARY}
        placeholder={placeHolder}
        secondaryLabel={secondaryLabel}
        onChange={onChangeInput}
        onBlur={handleManualDateValidate}
        onKeyDown={handleKeyDown}
      />
      <Modal
        show={isOpen}
        onClose={() => setIsOpen(false)}
        title={modalTitle ?? title}
        size={Sizes.SMALL}
        variant={Variants.PRIMARY}
      >
        <StyledModalCalendarContainer>
          <StyledCalendarContainer variant="small">
            <Calendar
              className="calendar"
              onChange={(value: Date, _: ChangeEvent<HTMLInputElement>) => handleChangeDate(value)}
              selectRange={false}
              value={currentDate ?? new Date()}
              locale={lang}
              showNeighboringMonth={false}
              view="month"
            />
          </StyledCalendarContainer>
        </StyledModalCalendarContainer>
      </Modal>
    </RootContainer>
  );
};
